import { useCallback } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Select from "ds/components/Select";
import SpaceliftLogoWithText from "ds/components/SpaceliftLogo";
import Textarea from "ds/components/Textarea";
import Typography from "ds/components/Typography";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import { stringIsRequired, validateEmail, validatePhoneNumber } from "utils/formValidators";

import {
  CHALLENGE_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  TYPE_OF_USE_OPTIONS,
  WORK_OPTIONS,
} from "./constants";
import { SUBMIT_PSU_FORM } from "./gql";
import styles from "./styles.module.css";
import { ProductSignUpFormFields } from "./types";

const useSubmitPSUForm = createDefaultMutationHook(SUBMIT_PSU_FORM);

const ProductSignUpForm = () => {
  const productSignUpForm = useForm<ProductSignUpFormFields>({
    defaultValues: {
      typeOfUse: "Business",
      workEmail: "",
      phoneNumber: "",
      job: "",
      jobOther: "",
      companySize: "",
      challenge: "",
      challengeOther: "",
    },
    mode: "onSubmit",
    shouldUnregister: true,
  });

  const [submitPSUForm, { loading }] = useSubmitPSUForm({
    refetchQueries: ["GetAccount"],
    awaitRefetchQueries: true,
  });

  const { control, watch, handleSubmit } = productSignUpForm;

  const currentTypeOfUse = watch("typeOfUse");
  const currentJob = watch("job");
  const currentChallenge = watch("challenge");

  const isBusiness = currentTypeOfUse === "Business";

  const isOtherJob = currentJob === "Other";
  const isOtherChallenge = currentChallenge === "Other";

  const prepareSubmitData = useCallback(
    async (data: ProductSignUpFormFields) => {
      const { jobOther, challengeOther, ...fields } = data;
      const preparedData = { ...fields };

      if (jobOther) {
        preparedData.job = jobOther;
      }

      if (challengeOther) {
        preparedData.challenge = challengeOther;
      }

      // Trim all string values
      Object.entries(preparedData).forEach(([key, value]) => {
        if (typeof value === "string") {
          preparedData[key as keyof typeof preparedData] = value.trim();
        }
      });

      submitPSUForm({ data: JSON.stringify(preparedData) });
    },
    [submitPSUForm]
  );

  return (
    <FormProvider {...productSignUpForm}>
      <Box grid gridTemplate="1fr 2fr" direction="row" className={styles.pageWrapper}>
        <Box padding="xx-large" className={styles.leftSide} direction="column">
          <SpaceliftLogoWithText className={styles.logo} />
        </Box>
        <Box className={styles.mainContent} direction="column">
          <form className={styles.form} onSubmit={handleSubmit(prepareSubmitData)}>
            <Box gap="x-large" direction="column">
              <Typography variant="p-t2" tag="h1" margin="0 0 xx-large" className={styles.title}>
                Welcome to Spacelift!
              </Typography>
              <Controller
                name="typeOfUse"
                control={control}
                render={({ field }) => (
                  <FormField
                    label="What will you use Spacelift for?"
                    helperText="Do you intend using Spacelift for professional or personal use?"
                    noMargin
                  >
                    {({ ariaInputProps }) => (
                      <Select
                        value={field.value}
                        items={TYPE_OF_USE_OPTIONS}
                        onChange={field.onChange}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                )}
              />
              {isBusiness && (
                <Controller
                  name="workEmail"
                  control={control}
                  rules={{ required: "Work email is required", validate: validateEmail }}
                  render={({ field, fieldState }) => (
                    <FormField
                      label="Work email"
                      helperText="Please provide us with your work email "
                      error={fieldState.error?.message}
                      noMargin
                    >
                      {({ ariaInputProps }) => (
                        <Input
                          error={!!fieldState.error}
                          type="text"
                          value={field.value}
                          placeholder="you@company.com"
                          onChange={field.onChange}
                          {...ariaInputProps}
                        />
                      )}
                    </FormField>
                  )}
                />
              )}
              {isBusiness && (
                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    validate: {
                      required: stringIsRequired("Phone number is required"),
                      phone: validatePhoneNumber,
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <FormField
                      label="Phone number"
                      helperText="Please provide us with your phone number"
                      error={fieldState.error?.message}
                      noMargin
                    >
                      {({ ariaInputProps }) => (
                        <Input
                          error={!!fieldState.error}
                          type="tel"
                          value={field.value}
                          placeholder="(555) 123-4567"
                          onChange={field.onChange}
                          {...ariaInputProps}
                        />
                      )}
                    </FormField>
                  )}
                />
              )}
              <Controller
                name="job"
                control={control}
                rules={{ required: "This is required" }}
                render={({ field, fieldState }) => (
                  <FormField
                    label="What kind of work do you do?"
                    helperText="What best describes your work?"
                    error={fieldState.error?.message}
                    noMargin
                  >
                    {({ ariaInputProps }) => (
                      <Select
                        value={field.value}
                        items={WORK_OPTIONS}
                        onChange={field.onChange}
                        isError={!!fieldState.error}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                )}
              />
              {isOtherJob && (
                <Controller
                  name="jobOther"
                  control={control}
                  rules={{ validate: stringIsRequired("This is required") }}
                  render={({ field, fieldState }) => (
                    <FormField error={fieldState.error?.message} noMargin>
                      {({ ariaInputProps }) => (
                        <Input
                          error={!!fieldState.error}
                          type="text"
                          value={field.value}
                          placeholder="Project Manager"
                          onChange={field.onChange}
                          {...ariaInputProps}
                        />
                      )}
                    </FormField>
                  )}
                />
              )}
              <Controller
                name="companySize"
                control={control}
                rules={{ required: "Company size is required" }}
                render={({ field, fieldState }) => (
                  <FormField
                    label="Company size"
                    helperText="How many people are working in your company?"
                    error={fieldState.error?.message}
                    noMargin
                  >
                    {({ ariaInputProps }) => (
                      <Select
                        value={field.value}
                        items={COMPANY_SIZE_OPTIONS}
                        onChange={field.onChange}
                        isError={!!fieldState.error}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="challenge"
                control={control}
                rules={{ required: "This is required" }}
                render={({ field, fieldState }) => (
                  <FormField
                    label="Biggest challenge"
                    error={fieldState.error?.message}
                    noMargin
                    helperText="What is the biggest challenge you're looking to solve with Spacelift?"
                  >
                    {({ ariaInputProps }) => (
                      <Select
                        value={field.value}
                        items={CHALLENGE_OPTIONS}
                        onChange={field.onChange}
                        isError={!!fieldState.error}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                )}
              />
              {isOtherChallenge && (
                <Controller
                  name="challengeOther"
                  control={control}
                  rules={{ validate: stringIsRequired("This is required") }}
                  render={({ field, fieldState }) => (
                    <FormField error={fieldState.error?.message} noMargin>
                      {({ ariaInputProps }) => (
                        <Textarea
                          error={!!fieldState.error}
                          value={field.value}
                          placeholder="Please describe your challenge..."
                          onChange={field.onChange}
                          {...ariaInputProps}
                        />
                      )}
                    </FormField>
                  )}
                />
              )}
              <Box justify="end">
                <Button
                  variant="primary"
                  type="submit"
                  loading={loading}
                  disabled={loading}
                  className={styles.submitButton}
                >
                  Get started!
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default ProductSignUpForm;
